import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateCreationQualifications } from "../../../store/slices/qualificationSlice";
import { Plus, X, Shield, Star, Globe, Award } from "lucide-react";

const Qualifications = () => {
  const dispatch = useDispatch();
  const qualifications = useSelector(
    (state) => state.qualification.creation.qualifications,
  );
  const [newQualification, setNewQualification] = useState({
    type: "",
    value: "",
    description: "",
  });

  const qualificationTypes = [
    { value: "location", label: "Location", icon: Globe },
    { value: "approval_rate", label: "Approval Rate", icon: Star },
    { value: "total_hits", label: "Total HITs Completed", icon: Award },
    { value: "custom", label: "Custom Qualification", icon: Shield },
  ];

  const addQualification = () => {
    if (newQualification.type && newQualification.value) {
      const updatedQualifications = [
        ...qualifications,
        { ...newQualification, id: Date.now() },
      ];
      dispatch(updateCreationQualifications(updatedQualifications));
      setNewQualification({ type: "", value: "", description: "" });
    }
  };

  const removeQualification = (id) => {
    const updatedQualifications = qualifications.filter((q) => q.id !== id);
    dispatch(updateCreationQualifications(updatedQualifications));
  };

  const getQualificationIcon = (type) => {
    const qualType = qualificationTypes.find((qt) => qt.value === type);
    return qualType ? qualType.icon : Shield;
  };

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Worker Qualifications
        </h2>
        <p className="text-gray-600">
          Set requirements for workers who can participate in your project
        </p>
      </div>

      {/* Existing Qualifications */}
      {qualifications.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Current Qualifications
          </h3>
          <div className="space-y-3">
            {qualifications.map((qual) => {
              const Icon = getQualificationIcon(qual.type);
              return (
                <div
                  key={qual.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-primary-600" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {qualificationTypes.find((qt) => qt.value === qual.type)
                          ?.label || "Custom"}
                      </div>
                      <div className="text-sm text-gray-600">
                        {qual.value}{" "}
                        {qual.description && `- ${qual.description}`}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeQualification(qual.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add New Qualification */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Add New Qualification
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Qualification Type *
            </label>
            <select
              value={newQualification.type}
              onChange={(e) =>
                setNewQualification({
                  ...newQualification,
                  type: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select qualification type</option>
              {qualificationTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value/Requirement *
            </label>
            <input
              type="text"
              value={newQualification.value}
              onChange={(e) =>
                setNewQualification({
                  ...newQualification,
                  value: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={
                newQualification.type === "location"
                  ? "e.g., US, CA, GB"
                  : newQualification.type === "approval_rate"
                    ? "e.g., 95%"
                    : newQualification.type === "total_hits"
                      ? "e.g., 1000"
                      : "Enter requirement value"
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <input
              type="text"
              value={newQualification.description}
              onChange={(e) =>
                setNewQualification({
                  ...newQualification,
                  description: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Additional details about this qualification"
            />
          </div>

          <button
            onClick={addQualification}
            disabled={!newQualification.type || !newQualification.value}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Qualification
          </button>
        </div>
      </div>
    </div>
  );
};

export default Qualifications;
